"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { HelpCircle, ChevronDown, Calendar } from "lucide-react" // Import Calendar icon

const faqItems = [
{
question: "How does Trendhopper automate blog creation?",
answer:
"Trendhopper uses AI to automatically generate one high-quality blog post per day tailored to your business niche. It handles everything from keyword research and content structuring to formatting and adding relevant photos with captions.",
},
{
question: "Can I approve or reject blog ideas before they're created?",
answer:
"Yes, Trendhopper allows you to review and approve or reject blog ideas before they are written. This ensures the content aligns with your business goals and target audience.",
},
{
question: "Can I provide feedback on drafts before publishing?",
answer:
"Trendhopper gives you the ability to review drafts and provide feedback. The AI refines the content based on your input to ensure it meets your expectations before publishing.",
},
{
question: "Can Trendhopper post blogs on my behalf?",
answer:
"Yes, Trendhopper integrates seamlessly with popular blogging platforms like WordPress, Ghost, and more. Once a blog is finalized, it can be scheduled or directly published, saving you time and effort.",
},
{
question: "How does Trendhopper handle keyword research?",
answer:
"Trendhopper automates the keyword research process by analyzing your niche, target audience, and search trends. Each blog post is crafted to rank for relevant keywords, ensuring maximum visibility and organic traffic.",
},
{
question: "Why is Trendhopper better than other blogging platforms?",
answer:
"Unlike other platforms, Trendhopper fully automates the blog creation process, including keyword research, photo generation, captions, and publishing. Additionally, it allows you to approve ideas, give feedback on drafts, and ensures that every blog post aligns with your vision and brand.",
},
{
question: "How does Trendhopper ensure the quality of blog posts?",
answer:
"Trendhopper's AI analyzes industry trends, writing styles, and SEO best practices to produce professional, engaging, and relevant content. Each blog is tailored to your business needs, complete with high-quality visuals and captions.",
},
]

export default function FaqSection() {
const [openIndex, setOpenIndex] = useState(0)

const generateEmailBody = () => {
return encodeURIComponent(
`Hi Matt,

I'm interested in booking a demo for TrendHopper.

My general availability is:

[Your Availability - e.g., Weekdays 2-5 PM PST]

Thanks!`
)
}

return (
<section
    id="faq"
    className="relative bg-gradient-to-b from-slate-900 via-slate-900 to-slate-900/90 py-24 overflow-hidden"
  >
{/* Background pattern */}
<div className="absolute inset-0 bg-grid-white/5 bg-grid [mask-image:radial-gradient(white,transparent_70%)]" />

<div className="container mx-auto max-w-4xl px-4 relative">
    <div className="text-center space-y-4 mb-16">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
        <span className="inline-flex items-center gap-2 text-orange-500 font-medium">
          <HelpCircle className="w-4 h-4" />
          Support
        </span>
        <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 tracking-tight">
          Frequently Asked Questions
        </h2>
      </motion.div>
    </div>

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="space-y-4"
    >
      {faqItems.map((item, index) => (
        <motion.div key={index} initial={false} className="group">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full text-left bg-slate-800/50 hover:bg-slate-800/70 backdrop-blur-sm p-6 rounded-2xl border border-slate-700/50 transition-all duration-200"
            aria-expanded={openIndex === index}
            aria-controls={`faq-${index}`}
          >
            <div className="flex justify-between items-center gap-4">
              <h3 className="text-xl font-semibold text-white group-hover:text-orange-400 transition-colors">
                {item.question}
              </h3>
              <ChevronDown
                className={`w-5 h-5 text-orange-400 transition-transform duration-200 ${
                  openIndex === index ? "rotate-180" : ""
                }`}
              />
            </div>
            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  id={`faq-${index}`}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <p className="text-slate-300 mt-4 leading-relaxed">{item.answer}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        </motion.div>
      ))}
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-12 text-center"
    >
      {/* Book a Demo Button */}
      <button
        className="group px-4 py-2 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-medium text-sm md:text-base rounded-full focus:outline-none hover:from-orange-600 hover:to-rose-600 shadow-md transition-transform transform hover:scale-105 flex items-center justify-center gap-2 mx-auto" // Added group class, flex, items-center, justify-center, gap-2
        onClick={() => {
          window.location.href = `mailto:matt@trendhopper.ai?subject=I would like to book a demo for TrendHopper&body=${generateEmailBody()}`
        }}
      >
        <Calendar className="w-4 h-4 group-hover:animate-bounce transition-all duration-200" /> {/* Added Calendar icon and hover animation */}
        Book a Demo
      </button>
    </motion.div>
  </div>
</section>  )
}
